import {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styles from "./Textarea.module.scss";
import InputWrapper, { InputWrapperProps } from "../InputWrapper/InputWrapper";
import TextareaAutosize from "react-textarea-autosize";
import mergeRefs from "merge-refs";
import classNames from "classnames";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";
import IconButton from "../IconButton/IconButton";
import { Expand } from "../Icon/Icon";

type TextInputProps = {
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  disableNewLine?: boolean;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  onFocus?: FocusEventHandler<HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>;
  onKeyUp?: KeyboardEventHandler<HTMLTextAreaElement>;
  onMouseDown?: MouseEventHandler<HTMLTextAreaElement>;
  onMouseUp?: MouseEventHandler<HTMLTextAreaElement>;
  disabled?: boolean;
  autosize?: boolean;
  minRows?: number;
  maxRows?: number;
  rows?: number;
  allowFullScreen?: boolean;
  fullScreenLabel?: string;
} & Omit<InputWrapperProps, "children" | "inputRef">;

const Textarea = forwardRef<HTMLTextAreaElement, TextInputProps>(
  function Textarea(
    {
      className,
      leftSection,
      rightSection,
      onChange,
      onClick,
      onBlur,
      onFocus,
      onKeyDown,
      onKeyUp,
      onMouseDown,
      onMouseUp,
      disableNewLine,
      value,
      placeholder,
      disabled,
      loading,
      loadingMessage,
      size,
      defaultValue,
      autosize,
      autofocus,
      unstyled,
      minRows = 3,
      maxRows,
      rows,
      allowFullScreen = false,
      fullScreenLabel,
    },
    ref,
  ) {
    const [fullScreen, setFullScreen] = useState(false);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        onKeyDown?.(event);

        if (disableNewLine && event.key === "Enter") {
          event.preventDefault();
        }
      },
      [disableNewLine, onKeyDown],
    );

    const innerRef = useRef<HTMLTextAreaElement>(null);
    const refs = mergeRefs(ref, innerRef);

    useEffect(() => {
      if (autofocus && innerRef.current) {
        innerRef.current.focus();
        innerRef.current.setSelectionRange(
          innerRef.current.value.length,
          innerRef.current.value.length,
        );
      }
    }, [autofocus]);

    return (
      <>
        {allowFullScreen && (
          <Modal
            title={fullScreenLabel}
            isOpen={fullScreen}
            onClose={() => setFullScreen(false)}
            size="full-width"
            fullHeight
          >
            <InputWrapper
              onClick={onClick}
              className={styles.fullScreenInput}
              leftSection={leftSection}
              rightSection={rightSection}
              disabled={disabled}
              size={size}
              unstyled={unstyled}
              loading={loading}
              loadingMessage={loadingMessage}
            >
              <textarea
                rows={rows}
                ref={refs}
                className={styles.input}
                defaultValue={defaultValue}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={onFocus}
                onKeyDown={handleKeyDown}
                onKeyUp={onKeyUp}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                value={value ?? ""}
                placeholder={placeholder}
                disabled={disabled}
              />
            </InputWrapper>
            <Button
              variant="outline"
              size="medium"
              onClick={() => setFullScreen(false)}
              className={styles.fullScreenClose}
            >
              Close
            </Button>
          </Modal>
        )}
        <InputWrapper
          onClick={onClick}
          className={classNames(styles.inputWrapper, className)}
          leftSection={leftSection}
          rightSection={
            allowFullScreen ? (
              <>
                {rightSection}
                <IconButton
                  className={styles.fullScreenOpen}
                  IconComponent={Expand}
                  onClick={() => setFullScreen(true)}
                  aria-label="Expand"
                  iconSize="small"
                />
              </>
            ) : (
              rightSection
            )
          }
          disabled={disabled}
          size={size}
          unstyled={unstyled}
          loading={loading}
          loadingMessage={loadingMessage}
        >
          {autosize ? (
            <TextareaAutosize
              rows={rows}
              minRows={minRows}
              maxRows={maxRows}
              ref={refs}
              className={classNames(styles.input, styles.inputAutosize, {
                [styles.inputAutosizeScroll]: typeof maxRows === "number",
              })}
              defaultValue={defaultValue}
              onChange={onChange}
              onBlur={onBlur}
              onFocus={onFocus}
              onKeyDown={handleKeyDown}
              onKeyUp={onKeyUp}
              onMouseDown={onMouseDown}
              onMouseUp={onMouseUp}
              value={!loading && value ? value : ""}
              placeholder={placeholder}
              disabled={disabled}
            />
          ) : (
            <textarea
              rows={rows}
              ref={refs}
              className={styles.input}
              defaultValue={defaultValue}
              onChange={onChange}
              onBlur={onBlur}
              onFocus={onFocus}
              onKeyDown={handleKeyDown}
              onKeyUp={onKeyUp}
              onMouseDown={onMouseDown}
              onMouseUp={onMouseUp}
              value={value ?? ""}
              placeholder={placeholder}
              disabled={disabled}
            />
          )}
        </InputWrapper>
      </>
    );
  },
);

export default Textarea;
